import Link from "next/link";
import { notFound, redirect } from "next/navigation";

import { auth } from "@/auth";
import { prisma } from "@/lib/prisma";

import ProductForm from "../../product-form";

type EditAdminArticlePageProps = {
  params: Promise<{
    id: string;
  }>;
};

export default async function EditAdminArticlePage({
  params,
}: EditAdminArticlePageProps) {
  const session = await auth();

  if (!session?.user) {
    redirect("/connexion?callbackUrl=/admin/articles");
  }

  if (session.user.role !== "ADMIN") {
    redirect("/compte");
  }

  const { id } = await params;

  const [product, categories] = await Promise.all([
    prisma.product.findUnique({
      where: { id },
      include: {
        images: {
          orderBy: {
            sortOrder: "asc",
          },
          select: {
            imageUrl: true,
          },
        },
      },
    }),
    prisma.category.findMany({
      orderBy: {
        name: "asc",
      },
      select: {
        id: true,
        name: true,
      },
    }),
  ]);

  if (!product) {
    notFound();
  }

  return (
    <div className="space-y-3 md:space-y-5 xl:space-y-6">
      <section className="overflow-hidden rounded-[1.75rem] border border-[#f2d7e4] bg-[linear-gradient(135deg,#fff8fb_0%,#fffdfa_58%,#fdf4f7_100%)] shadow-[0_16px_48px_rgba(196,78,124,0.07)]">
        <div className="grid gap-4 px-4 py-4 sm:gap-5 sm:px-5 sm:py-5 lg:grid-cols-[minmax(0,1fr)_minmax(240px,300px)] lg:items-end xl:grid-cols-[minmax(0,1.1fr)_280px]">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#9f8692] sm:text-xs">
              <Link
                href="/admin/articles"
                className="font-[family-name:var(--font-ibm-plex-mono)] uppercase tracking-[0.2em] transition hover:text-[#6f324e]"
              >
                Articles
              </Link>
              <span>/</span>
              <span className="font-[family-name:var(--font-ibm-plex-mono)] uppercase tracking-[0.18em] text-[#b64678]">
                Modifier
              </span>
            </div>

            <span className="inline-flex w-fit items-center rounded-full border border-[#efc7d9] bg-white/85 px-2.5 py-1 font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.22em] text-[#b64678] backdrop-blur">
              Fiche produit
            </span>

            <div className="space-y-2">
              <h1 className="max-w-[12ch] text-[2rem] font-semibold leading-[0.95] tracking-[-0.05em] text-[#24161d] sm:max-w-none sm:text-[2.35rem] lg:text-[2.8rem]">
                Modifier l&apos;article
              </h1>
              <p className="max-w-[58ch] text-[13px] leading-6 text-[#6f5a63] sm:text-sm sm:leading-6">
                Un espace plus éditorial pour ajuster la fiche de{" "}
                <span className="font-medium text-[#24161d]">{product.name}</span>,
                affiner ses informations et garder une cohérence parfaite avec le
                catalogue Jour J.
              </p>
            </div>

            <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
              <Link
                href="/admin/articles"
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-[#efc7d9] bg-white/85 px-4 py-2 text-sm font-medium text-[#6f324e] transition hover:border-[#e3a9c5] hover:bg-white"
              >
                Retour à la liste
              </Link>
              <Link
                href={`/produit/${product.slug}`}
                className="inline-flex min-h-10 items-center justify-center rounded-full bg-[#d85c92] px-4 py-2 text-sm font-medium text-white shadow-[0_10px_24px_rgba(216,92,146,0.22)] transition hover:bg-[#c94b82]"
              >
                Voir la fiche
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2.5 lg:grid-cols-1">
            <div className="rounded-[1.35rem] border border-white/80 bg-white/80 p-3.5 shadow-[0_8px_22px_rgba(36,22,29,0.045)] backdrop-blur">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[#a88495]">
                Statut
              </p>
              <p className="mt-2 text-sm font-medium text-[#24161d]">
                {product.isPublished ? "Publié" : "Brouillon"}
              </p>
            </div>
            <div className="rounded-[1.35rem] border border-white/80 bg-white/80 p-3.5 shadow-[0_8px_22px_rgba(36,22,29,0.045)] backdrop-blur">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[#a88495]">
                Stock
              </p>
              <p className="mt-2 text-sm font-medium text-[#24161d]">
                {product.stock} unité{product.stock > 1 ? "s" : ""}
              </p>
            </div>
            <div className="rounded-[1.35rem] border border-white/80 bg-white/80 p-3.5 shadow-[0_8px_22px_rgba(36,22,29,0.045)] backdrop-blur">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[#a88495]">
                Catégorie
              </p>
              <p className="mt-2 text-sm font-medium text-[#24161d]">
                {categories.find((category) => category.id === product.categoryId)
                  ?.name ?? "Sans catégorie"}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="rounded-[1.75rem] border border-[#f0dfe7] bg-[#fffdfb] p-4 shadow-[0_10px_28px_rgba(36,22,29,0.035)] sm:p-5">
        <div className="mb-4 flex flex-col gap-1.5">
          <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[#b38a9d]">
            Édition
          </p>
          <h2 className="text-xl font-semibold tracking-[-0.04em] text-[#24161d] sm:text-[1.7rem]">
            Mettre à jour la fiche
          </h2>
        </div>

        <ProductForm
          mode="edit"
          categories={categories}
          product={{
            id: product.id,
            name: product.name,
            slug: product.slug,
            sku: product.sku,
            shortDescription: product.shortDescription,
            description: product.description,
            price: Number(product.price),
            compareAtPrice: product.compareAtPrice
              ? Number(product.compareAtPrice)
              : null,
            stock: product.stock,
            categoryId: product.categoryId,
            isPublished: product.isPublished,
            isFeatured: product.isFeatured,
            isGlitter: product.isGlitter,
            material: product.material,
            color: product.color,
            careInstructions: product.careInstructions,
            seoTitle: product.seoTitle,
            seoDescription: product.seoDescription,
            images: product.images.map((image) => ({
              url: image.imageUrl,
            })),
          }}
        />
      </div>
    </div>
  );
}